import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useRealtimeStore } from '@/hooks/useRealtime'
import { useTerminalStore } from '@/hooks/useTerminal'
import { useAgents } from '@/hooks/useAgents'
import { useRecentActivity } from '@/hooks/useRecentActivity'
import { format } from 'date-fns'
import { clsx } from 'clsx'
import type { AgentFeedMessage, TerminalLine } from '@/lib/types'
import { api } from '@/lib/api'
import TickerLink from '@/components/ui/TickerLink'
import { CommandPalette } from '@/components/terminal/CommandPalette'
import { TickerStrip } from '@/components/terminal/TickerStrip'
import { FKeyShortcuts } from '@/components/terminal/FKeyShortcuts'
import { MultiPaneView } from '@/components/terminal/MultiPaneView'
import { ThemePicker, themeClass, useTerminalTheme } from '@/components/terminal/ThemePicker'
import { SnapshotButton } from '@/components/terminal/SnapshotButton'
import { ShortcutOverlay } from '@/components/terminal/ShortcutOverlay'
import { ReverseSearch } from '@/components/terminal/ReverseSearch'
import { NavIndicator } from '@/components/terminal/NavIndicator'
import { ConcentrationChip } from '@/components/terminal/ConcentrationChip'
import { isPinned, pinLine, unpinLine } from '@/lib/terminal-pinned'

// ── Agent label mapping (for system messages) ──────────────────────────────

const AGENT_LABELS: Record<string, string> = {
  aria: 'ARIA',
  portfolio: 'POA',
  risk: 'RMA',
  market: 'MIA',
  compliance: 'CMA',
  goals: 'GTA',
  orchestrator: 'ORC',
}

// ── All available commands for autocomplete ────────────────────────────────

const ALL_COMMANDS = [
  // Macros / workflow
  { cmd: '/focus', alias: null, desc: 'Ticker deep-dive (quote + chart + news + earnings)' },
  { cmd: '/watch', alias: null, desc: 'Watchlist — /watch add TICKER, /watch ls, /watch rm TICKER' },
  { cmd: '/grep',  alias: null, desc: 'Search terminal output for a pattern (client-side)' },
  { cmd: '/alias', alias: null, desc: 'Define / list / remove command aliases (persists in browser)' },
  { cmd: '/note',  alias: '/notes', desc: 'Per-ticker notes — /note AAPL <text>, /note AAPL, /note list' },
  { cmd: '/pinned', alias: null, desc: 'Show pinned output lines (hover ★ to pin)' },
  { cmd: '/reprice', alias: '/mtm /refresh-prices', desc: 'Force mark-to-market all holdings from live sources' },
  { cmd: '/fx-exposure', alias: '/fxe /currency', desc: 'Portfolio currency exposure (GBP vs USD vs other)' },
  { cmd: '/fx-rates', alias: '/rates', desc: 'Live FX rates used by the valuation pipeline' },
  { cmd: '/pricing-health', alias: '/ph', desc: 'Probe every pricing source (MSN/CoinGecko/FT/FMP/cache)' },
  { cmd: '/coverage', alias: '/dq', desc: 'Holdings coverage % + freshness breakdown' },
  { cmd: '/coverage-by-account', alias: '/dq-by-account', desc: 'Per-account coverage — shows where to focus ISIN entry' },
  { cmd: '/fx-by-account', alias: '/fxe-by-acc', desc: 'Per-account currency breakdown (find hidden USD)' },
  { cmd: '/timeline', alias: '/reprice-history', desc: 'Last N reprice runs with NAV deltas' },
  { cmd: '/whatsnew', alias: '/changelog', desc: 'Recent commits — what shipped on the backend' },
  { cmd: '/concentration', alias: '/conc /top-holdings', desc: 'Top holdings by % of tracked investments (risk view)' },
  { cmd: '/dashboard', alias: '/pf-health', desc: 'Single-call portfolio dashboard (NAV + deltas + coverage + concentration)' },
  { cmd: '/movers-today', alias: '/mov-today /today-movers', desc: 'Top GBP movers in your portfolio today (winners + losers)' },
  { cmd: '/benchmark', alias: '/bm /vs', desc: 'Portfolio return vs SPY/QQQ/VUAG over a window (1m/3m/6m/1y)' },
  { cmd: '/version', alias: '/v', desc: 'Deployed git SHA + last commit + env config' },
  { cmd: '/budget', alias: '/fmp-budget', desc: 'FMP API budget today (200/day soft cap)' },
  { cmd: '/risk-decomp', alias: '/rd /risk-attribution', desc: 'Per-holding variance contribution + diversification ratio' },
  { cmd: '/holdings', alias: '/h', desc: 'Top holdings flat across all accounts (sortable, filterable)' },
  { cmd: '/backtest', alias: '/bt', desc: 'Backtest current portfolio weights vs SPY/QQQ/VUAG (1m–5y)' },
  { cmd: '/strategy-compare', alias: '/sc /strategies', desc: 'Rank every strategy on a ticker by Sharpe (which strategy fits best?)' },
  { cmd: '/optimize', alias: '/opt /grid-search', desc: 'Grid-search optimal stop-loss / take-profit / position size for a strategy' },
  { cmd: '/gain-harvest', alias: '/cgt-harvest /tax-gains', desc: 'Sell suggestions to use £3k CGT annual exempt amount' },
  { cmd: '/income-projection', alias: '/income-proj /projected-income', desc: 'Annual dividend + interest income from current holdings' },
  { cmd: '/sector-rotation', alias: '/sector-mix /rotation', desc: 'Portfolio sector exposure with tilt vs S&P 500' },
  { cmd: '/dividend-calendar', alias: '/div-cal /dividends-upcoming', desc: 'Estimated upcoming dividends from holdings (top 30 by value)' },
  { cmd: '/asset-mix', alias: '/asset-class /allocation', desc: 'Asset class breakdown — risk-on / fixed income / cash / property' },
  { cmd: '/sip-overview', alias: '/sipp /sipp-summary', desc: 'SIPP composition: total, top 10, sector mix, ISIN coverage' },
  { cmd: '/log', alias: '/note-trade /journal-quick', desc: 'Quick journal entry — /log TICKER body [--rating=N --action=LONG]' },
  { cmd: '/holdings-audit', alias: '/audit-holdings /who-changed', desc: 'Recent ADD/EDIT/DEL to your holdings — who & when' },
  // Equity
  { cmd: '/quote', alias: '/q BQ QR', desc: 'Real-time quote' },
  { cmd: '/chart', alias: '/c GP GIP COMP', desc: 'Historical chart' },
  { cmd: '/profile', alias: '/p DES FA', desc: 'Company profile' },
  { cmd: '/earnings', alias: '/e ERN EA', desc: 'Earnings history' },
  { cmd: '/dividends', alias: '/div DVD', desc: 'Dividend data' },
  { cmd: '/technicals', alias: 'TA', desc: 'Technical analysis' },
  { cmd: '/compare', alias: null, desc: 'Compare stocks side-by-side' },
  { cmd: '/options', alias: '/opt', desc: 'Options chain' },
  // Valuation
  { cmd: '/wacc', alias: null, desc: 'Weighted avg cost of capital' },
  { cmd: '/dcf', alias: null, desc: 'DCF valuation model' },
  { cmd: '/ddm', alias: null, desc: 'Dividend discount model' },
  { cmd: '/financials', alias: 'FA', desc: 'Key financial metrics' },
  { cmd: '/peers', alias: null, desc: 'Peer comparison table' },
  { cmd: '/targets', alias: 'TGT', desc: 'Analyst price targets' },
  { cmd: '/ratings', alias: 'RATD', desc: 'Analyst ratings distribution' },
  // Market
  { cmd: '/indices', alias: '/idx WB INDU NKY DAX', desc: 'Market indices' },
  { cmd: '/movers', alias: 'MOV', desc: 'Top gainers & losers' },
  { cmd: '/sectors', alias: 'S5SECT', desc: 'Sector performance' },
  { cmd: '/bonds', alias: 'GB', desc: 'Treasury yields' },
  { cmd: '/vix', alias: null, desc: 'VIX volatility index' },
  { cmd: '/forex', alias: '/fx FXIP FXC', desc: 'Major forex rates' },
  { cmd: '/crncy', alias: 'FXCM', desc: 'FX cross-rate matrix' },
  { cmd: '/commodities', alias: 'CMD CMDTY', desc: 'Commodity futures' },
  { cmd: '/country', alias: null, desc: 'Country economic dashboard' },
  { cmd: '/surprise', alias: null, desc: 'Economic surprise index (actual vs consensus)' },
  { cmd: '/global', alias: null, desc: 'Global heatmap; /global gdp|inflation|unemployment (World Bank)' },
  { cmd: '/g7', alias: null, desc: 'G7 economy snapshot (World Bank live data)' },
  { cmd: '/g20', alias: null, desc: 'G20 economy snapshot (World Bank live data)' },
  { cmd: '/crypto', alias: 'CTOP', desc: 'Crypto (top/detail/trending/global/defi)' },
  { cmd: '/topvol', alias: null, desc: 'Top volume stocks' },
  // Alt Data
  { cmd: '/insider', alias: 'ANR BI', desc: 'Insider trades' },
  { cmd: '/congress', alias: null, desc: 'Congressional trades' },
  { cmd: '/guru', alias: null, desc: 'Guru portfolios' },
  { cmd: '/shorts', alias: 'SI MOST', desc: 'Short interest' },
  { cmd: '/squeeze', alias: null, desc: 'Squeeze candidates' },
  { cmd: '/sentiment', alias: 'SALT', desc: 'Social sentiment' },
  { cmd: '/wsb', alias: null, desc: 'WSB trending' },
  { cmd: '/fear', alias: null, desc: 'Fear & Greed Index' },
  { cmd: '/flows', alias: null, desc: 'ETF sector rotation' },
  { cmd: '/contracts', alias: null, desc: 'Government contracts' },
  { cmd: '/patents', alias: null, desc: 'Patent activity' },
  { cmd: '/lobbying', alias: null, desc: 'Lobbying spenders' },
  // Market Structure
  { cmd: '/cacs', alias: 'CACS', desc: 'Corporate actions' },
  { cmd: '/spread', alias: null, desc: 'Bid/ask spread' },
  { cmd: '/float', alias: null, desc: 'Float & ownership' },
  // Options
  { cmd: '/options-flow', alias: '/oflow OMON OV', desc: 'Unusual options flow' },
  { cmd: '/pcr', alias: null, desc: 'Put/call ratio' },
  { cmd: '/maxpain', alias: null, desc: 'Options max pain' },
  // Volatility
  { cmd: '/vol scan', alias: null, desc: 'Vol arbitrage scanner (IV vs RV)' },
  { cmd: '/vol surface', alias: null, desc: 'Volatility surface (smile/skew)' },
  { cmd: '/vol squeeze', alias: null, desc: 'Volatility squeeze detector' },
  { cmd: '/vol term', alias: null, desc: 'Vol term structure' },
  // Prediction Markets
  { cmd: '/predictions', alias: '/polymarket', desc: 'Trending prediction markets' },
  { cmd: '/elections', alias: null, desc: 'Election / political odds' },
  { cmd: '/econ-predict', alias: null, desc: 'Economic predictions' },
  // Portfolio
  { cmd: '/income', alias: null, desc: 'Income summary — dividends, interest, staking, prizes' },
  { cmd: '/rebalance', alias: null, desc: 'Discretionary portfolio breakdown + suggestions' },
  { cmd: '/compare-models', alias: null, desc: 'Portfolio vs model portfolios (60/40, Vanguard LS80, All-Weather, Aggressive)' },
  { cmd: '/what-would', alias: null, desc: 'Persona portfolio critique: /what-would buffett | dalio | burry | soros | cathie | simons' },
  { cmd: '/whatif', alias: null, desc: 'What-if scenario: /whatif sell BATS.L 200 | /whatif buy NVDA £5000' },
  { cmd: '/target', alias: null, desc: 'Target allocation trades: /target balanced | /target aggressive | /target custom UK:25 US:30 ...' },
  { cmd: '/portfolio', alias: '/pf PORT', desc: 'Portfolio summary' },
  { cmd: '/positions', alias: '/pos PMON', desc: 'Open positions & P&L' },
  { cmd: '/risk', alias: 'MARS VAR', desc: 'Risk dashboard' },
  { cmd: '/stress', alias: null, desc: 'Stress test' },
  { cmd: '/pot', alias: null, desc: 'Discretionary pot' },
  { cmd: '/pnl', alias: null, desc: 'P&L summary' },
  { cmd: '/performance', alias: '/perf', desc: 'NAV chart + rolling metrics' },
  { cmd: '/sharpe', alias: null, desc: 'Rolling Sharpe ratio' },
  { cmd: '/close', alias: null, desc: 'Close a position' },
  { cmd: '/goals', alias: null, desc: 'Goals & probabilities' },
  { cmd: '/watchlist', alias: '/wl', desc: 'Current watchlist' },
  // Analysis
  { cmd: '/tra', alias: null, desc: 'Trade analysis (win rate, P&L)' },
  // Agents
  { cmd: '/analyze', alias: '/run', desc: 'Run agent analysis' },
  { cmd: '/recommendations', alias: '/recs', desc: 'Pending recommendations' },
  { cmd: '/consensus', alias: null, desc: 'Agent consensus' },
  { cmd: '/signals', alias: null, desc: 'Persona signals for portfolio or ticker' },
  { cmd: '/alerts test', alias: null, desc: 'Send a test alert (in-app + Telegram + Discord)' },
  // Dashboards
  { cmd: '/ftse100', alias: 'UKX', desc: 'FTSE 100 dashboard' },
  { cmd: '/sp500', alias: 'SPX', desc: 'S&P 500 dashboard' },
  { cmd: '/nasdaq', alias: null, desc: 'NASDAQ dashboard' },
  { cmd: '/sector', alias: null, desc: 'Sector deep-dive' },
  { cmd: '/macro', alias: 'ECO ECFC WIRP WEI WECO', desc: 'Macro dashboard' },
  { cmd: '/pd', alias: null, desc: 'Portfolio dashboard' },
  { cmd: '/screener', alias: 'GRAB EQS SECF RV', desc: 'Screener results' },
  { cmd: '/scan', alias: null, desc: 'Quick scan (momentum, oversold, ...)' },
  { cmd: '/news', alias: 'TOP N NI CN', desc: 'Latest headlines' },
  { cmd: '/calendar', alias: 'EVTS', desc: 'Events calendar' },
  { cmd: '/ipo', alias: null, desc: 'Upcoming IPOs' },
  // Messaging
  { cmd: '/msg', alias: null, desc: 'Send message: /msg #trading <text> | /msg @unman <text>' },
  { cmd: '/messages', alias: null, desc: 'Show recent messages: /messages | /messages #trading' },
  { cmd: '/rooms', alias: null, desc: 'List Matrix chat rooms' },
  // Workspace & System
  { cmd: '/layout', alias: null, desc: 'Manage layouts' },
  { cmd: '/help', alias: 'HELP', desc: 'Command reference' },
  { cmd: '/status', alias: null, desc: 'System status' },
  { cmd: '/clear', alias: null, desc: 'Clear terminal' },
  // Bloomberg aliases as standalone entries for discoverability
  { cmd: '/des', alias: null, desc: 'Company description (-> /profile)' },
  { cmd: '/bq', alias: null, desc: 'Bloomberg quote (-> /quote)' },
  { cmd: '/ta', alias: null, desc: 'Technical analysis (-> /technicals)' },
  { cmd: '/mov', alias: null, desc: 'Market movers (-> /movers)' },
  { cmd: '/gb', alias: null, desc: 'Govt bonds (-> /bonds)' },
  { cmd: '/evts', alias: null, desc: 'Events (-> /calendar)' },
]

// ── Autocomplete types + fuzzy matcher ──────────────────────────────────────

type CommandEntry = { cmd: string; alias: string | null; desc: string }

/** Commands that take a ticker/symbol as their first argument — those get
 *  argument autocomplete from the user's holdings + watchlist. */
const TICKER_ARG_COMMANDS = new Set([
  '/quote', '/q', '/chart', '/c', '/focus', '/technicals', '/ta', '/options', '/opt',
  '/news', '/earnings', '/e', '/profile', '/signals', '/note', '/notes', '/set',
  '/analyze', '/risk', '/fundamentals', '/whale', '/montecarlo', '/mc', '/watch',
])

/**
 * Subsequence fuzzy score: does `query` appear in `target` in order (not
 * necessarily contiguous)? Returns a score (higher = better) or -1 for no
 * match. Prefix matches and contiguous runs rank highest, so '/cnvg' →
 * '/convergences' matches but '/convergences' itself ranks above a looser hit.
 */
function fuzzyScore(query: string, target: string): number {
  if (!query) return 0
  query = query.toLowerCase()
  target = target.toLowerCase()
  if (target.startsWith(query)) return 1000 - target.length  // prefer shorter exact-prefix
  let qi = 0
  let score = 0
  let streak = 0
  let prevIdx = -1
  for (let ti = 0; ti < target.length && qi < query.length; ti++) {
    if (target[ti] === query[qi]) {
      streak = prevIdx === ti - 1 ? streak + 1 : 1
      score += streak * 3 + (ti === 0 ? 5 : 0)
      prevIdx = ti
      qi++
    }
  }
  return qi === query.length ? score : -1
}

// ── Startup banner ─────────────────────────────────────────────────────────

const STARTUP_BANNER = [
  '  \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550',
  '   AgentVault Capital \u2014 Private Fund Terminal',
  '   Type /help for available commands',
  '   Data: yfinance | Finnhub | CoinGecko',
  '  \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550',
].join('\n')

// ── Rich content parser ────────────────────────────────────────────────────
//
// Splits a plain-text line into segments and replaces:
//   https://...          → <a> external link
//   /app/...             → React Router <Link>
//   $AAPL or bare tickers in context  → <TickerLink>

const URL_RE      = /(https?:\/\/[^\s,)"']+)/g
const INTERNAL_RE = /(\/app\/[^\s,)"']+)/g
// Ticker: explicit $TICKER or bare ALL-CAPS word 2-5 chars (optionally .L/.AS etc.)
const TICKER_RE   = /(\$[A-Z]{1,5}(?:\.[A-Z]{1,2})?|\b(?<![/])([A-Z]{1,5})(?=\s|$|[,.:;)]|\s))/g

// Common English words and finance abbreviations that should never be linked
const COMMON_WORDS = new Set([
  'THE', 'AND', 'FOR', 'ARE', 'NOT', 'BUT', 'HAS', 'WAS', 'HIS', 'HER',
  'ALL', 'CAN', 'HAD', 'HIM', 'HOW', 'ITS', 'MAY', 'NEW', 'NOW', 'OLD',
  'OUR', 'OUT', 'OWN', 'SAY', 'SHE', 'TOO', 'USE', 'WAY', 'WHO', 'BOY',
  'DID', 'GET', 'LET', 'MAN', 'PUT', 'RUN', 'SET', 'TOP', 'TRY', 'USD',
  'GBP', 'EUR', 'JPY', 'ETF', 'GDP', 'API', 'MFA', 'FAQ', 'PDF', 'URL',
  'CSS', 'IPO', 'CEO', 'CFO', 'CTO', 'COO', 'NHS', 'BBC', 'USA', 'ISA',
  'GIA', 'AUM', 'NAV', 'ROE', 'ROI', 'EPS', 'CGT', 'IHT', 'DTA', 'NHR',
  'PER', 'NET', 'TAX', 'BUY', 'LOW', 'MAX', 'MIN', 'AVG', 'SUM', 'DAY',
  'YTD', 'MTD', 'QTD',
])

type Segment =
  | { kind: 'text';     text: string }
  | { kind: 'url';      href: string }
  | { kind: 'internal'; path: string }
  | { kind: 'ticker';   symbol: string }
  | { kind: 'color';    text: string; color: string }

// ── Colour markup (§<code><text>§) → Tailwind class ────────────────
// Used by the Python terminal helpers (_c_green, _c_red, _c_pnl, …)
// to emit signed-value colouring from the backend. Keep short.
const COLOR_CLASS: Record<string, string> = {
  g: 'text-crt-green',
  r: 'text-red-400',
  a: 'text-crt-amber',
  c: 'text-crt-cyan',
  b: 'text-crt-bright font-bold',
  d: 'text-crt-dim',
  e: 'text-emerald-300',
  p: 'text-purple-300',
}

function parseSegments(raw: string): Segment[] {
  // Phase 1 — strip out colour-markup spans first so ticker regex etc.
  // doesn't try to match things INSIDE a coloured span.
  const segments: Segment[] = []
  const COLOR_RE = /§([grabcdep])([^§]+)§/g
  let lastEnd = 0
  let m: RegExpExecArray | null
  const remaining: { start: number; end: number; text: string }[] = []

  while ((m = COLOR_RE.exec(raw)) !== null) {
    if (m.index > lastEnd) {
      remaining.push({ start: lastEnd, end: m.index, text: raw.slice(lastEnd, m.index) })
    }
    // Push the coloured segment now — its text isn't further parsed
    remaining.push({
      start: m.index,
      end: COLOR_RE.lastIndex,
      text: '',
      // We'll sub in a coloured segment below
    } as any)
    segments.push({ kind: 'color', color: m[1], text: m[2] })
    lastEnd = COLOR_RE.lastIndex
  }
  if (lastEnd < raw.length) {
    remaining.push({ start: lastEnd, end: raw.length, text: raw.slice(lastEnd) })
  }

  // Early-out: if no colour markup found, skip the stitching and run the
  // legacy parser on the whole string.
  if (segments.length === 0) {
    return _parseTickersAndLinks(raw)
  }

  // Stitch: walk `remaining` in order, either pulling a coloured segment
  // (when its position matches) or running the legacy parser on plain text.
  const out: Segment[] = []
  const colorQueue = [...segments]
  for (const r of remaining) {
    if (r.text) {
      out.push(..._parseTickersAndLinks(r.text))
    } else if (colorQueue.length) {
      out.push(colorQueue.shift()!)
    }
  }
  return out
}

function _parseTickersAndLinks(raw: string): Segment[] {
  const combined = new RegExp(
    `(https?:\\/\\/[^\\s,)"']+)` +           // group 1: external URL
    `|(\\/app\\/[^\\s,)"']+)` +               // group 2: internal route
    `|(\\$[A-Z]{1,5}(?:\\.[A-Z]{1,2})?)` +   // group 3: $TICKER
    `|\\b([A-Z]{2,5}(?:\\.[A-Z]{1,2})?)\\b`, // group 4: bare TICKER / LLOY.L
    'g',
  )

  const segments: Segment[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = combined.exec(raw)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ kind: 'text', text: raw.slice(lastIndex, match.index) })
    }
    if (match[1]) {
      segments.push({ kind: 'url', href: match[1] })
    } else if (match[2]) {
      segments.push({ kind: 'internal', path: match[2] })
    } else if (match[3]) {
      segments.push({ kind: 'ticker', symbol: match[3].replace('$', '') })
    } else if (match[4]) {
      const word = match[4]
      if (COMMON_WORDS.has(word)) {
        segments.push({ kind: 'text', text: word })
      } else {
        segments.push({ kind: 'ticker', symbol: word })
      }
    }
    lastIndex = combined.lastIndex
  }

  if (lastIndex < raw.length) {
    segments.push({ kind: 'text', text: raw.slice(lastIndex) })
  }
  return segments
}

function RichLine({ text, className }: { text: string; className?: string }) {
  const segments = useMemo(() => parseSegments(text), [text])
  // If nothing special was found, short-circuit to plain rendering
  if (segments.length === 1 && segments[0].kind === 'text') {
    return <span className={className}>{text}</span>
  }
  return (
    <span className={className}>
      {segments.map((seg, i) => {
        switch (seg.kind) {
          case 'url':
            return (
              <a
                key={i}
                href={seg.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-crt-cyan underline hover:text-cyan-300 transition-colors"
                onClick={e => e.stopPropagation()}
              >
                {seg.href}
              </a>
            )
          case 'internal':
            return (
              <Link
                key={i}
                to={seg.path}
                className="text-crt-cyan underline hover:text-cyan-300 transition-colors"
                onClick={e => e.stopPropagation()}
              >
                {seg.path}
              </Link>
            )
          case 'ticker':
            return (
              <TickerLink key={i} ticker={seg.symbol} />
            )
          case 'color':
            return (
              <span key={i} className={COLOR_CLASS[seg.color] ?? ''}>
                {seg.text}
              </span>
            )
          default:
            return <span key={i}>{seg.text}</span>
        }
      })}
    </span>
  )
}

// ── Component ──────────────────────────────────────────────────────────────

export default function Terminal() {
  const [input, setInput] = useState('')
  const [historyIndex, setHistoryIndex] = useState(-1)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [selectedSuggestion, setSelectedSuggestion] = useState(0)
  // Full command catalog (all 720+ registered commands + aliases) and the
  // user's tickers — both fetched once on mount to drive autocomplete. Falls
  // back to the static ALL_COMMANDS list if the fetch fails.
  const [allCommands, setAllCommands] = useState<CommandEntry[]>(ALL_COMMANDS)
  const [tickerPool, setTickerPool] = useState<string[]>([])

  const inputRef = useRef<HTMLInputElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const processedCountRef = useRef(0)
  const startupDone = useRef(false)
  const userScrolledUp = useRef(false)

  const location = useLocation()
  const { connected, messages } = useRealtimeStore()
  const { history, commandHistory, executing, executeCommand, clear, addSystemMessage, addLine } =
    useTerminalStore()
  const { addSearch } = useRecentActivity()

  // Ctrl+K / Cmd+K command palette
  const [paletteOpen, setPaletteOpen] = useState(false)
  // Multi-pane split view (2x2 quadrants) — Ctrl+Shift+M toggles
  const [multiPaneOpen, setMultiPaneOpen] = useState(false)
  // Keyboard shortcut cheat-sheet — `?` when input is empty
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  // Ctrl+R reverse history search bar
  const [reverseOpen, setReverseOpen] = useState(false)
  // Pinned lines — state kept just to force re-render of star states
  const [pinTick, setPinTick] = useState(0)
  // Colour theme — green / amber / blue / bloomberg / paper
  const [theme, setTheme] = useTerminalTheme()

  // ── Startup: show banner and auto-run /indices ─────────────────────────

  useEffect(() => {
    if (startupDone.current) return
    startupDone.current = true

    addLine({ type: 'system', content: STARTUP_BANNER, timestamp: now() })

    const connMsg = connected
      ? 'WebSocket connected -- receiving live agent feed'
      : 'WebSocket connecting...'
    addSystemMessage(connMsg)

    // Check for auto-run command from navigation state (e.g. quick action buttons)
    // or from sessionStorage (e.g. TickerLink context menu)
    const navCmd = (location.state as { initialCommand?: string } | null)?.initialCommand
    const autorun = navCmd || sessionStorage.getItem('terminal-autorun')
    if (autorun) {
      if (!navCmd) sessionStorage.removeItem('terminal-autorun')
      setTimeout(() => {
        executeCommand(autorun)
        addSearch(autorun)
      }, 500)
    } else {
      // Auto-run /indices on startup (delay to let yfinance warm up)
      setTimeout(() => {
        executeCommand('/indices')
      }, 1500)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Global shortcuts: Ctrl+K palette, Ctrl+Shift+M multi-pane, Esc close ──
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k' && !e.shiftKey) {
        e.preventDefault()
        setPaletteOpen(p => !p)
        return
      }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'M' || e.key === 'm')) {
        e.preventDefault()
        setMultiPaneOpen(p => !p)
      }
      // `?` shortcut overlay — only when no modifier and input is empty
      // (so users can still type "?" into a command like /search).
      if (
        e.key === '?' &&
        !e.ctrlKey && !e.metaKey && !e.altKey &&
        (e.target as HTMLElement)?.tagName !== 'INPUT' &&
        (e.target as HTMLElement)?.tagName !== 'TEXTAREA'
      ) {
        e.preventDefault()
        setShortcutsOpen(o => !o)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  const handlePaletteSelect = useCallback((command: string, execute: boolean) => {
    if (execute) {
      executeCommand(command)
      addSearch(command)
    } else {
      setInput(command + ' ')
      setTimeout(() => inputRef.current?.focus(), 10)
    }
  }, [executeCommand, addSearch])

  // ── Process WebSocket messages as system messages ──────────────────────

  useEffect(() => {
    if (messages.length <= processedCountRef.current) return
    const newMessages = messages.slice(processedCountRef.current)
    processedCountRef.current = messages.length

    for (const msg of newMessages) {
      if (msg.type === 'pong') continue
      const d = msg.data ?? {}
      const agentId = d.agent_id ?? 'SYS'
      const label = AGENT_LABELS[agentId] ?? agentId.toUpperCase().slice(0, 4)
      let text = ''

      switch (msg.type) {
        case 'trade_executed':
          text = `[${label}] TRADE ${d.action ?? ''} ${d.ticker ?? ''} | ${d.title ?? ''}${d.paper_mode ? ' [PAPER]' : ''}`
          break
        case 'alert':
          text = `[${label}] ALERT: ${d.title ?? ''}`
          break
        case 'recommendation':
          text = `[${label}] ${d.action ?? ''} ${d.ticker ?? ''} -- ${d.title ?? ''}${d.confidence != null ? ` (${d.confidence}%)` : ''}`
          break
        default:
          // Only surface messages that actually carry a human-readable payload —
          // a bare event with no title/reasoning was printing "[SYS] undefined".
          text = d.title
            ? `[${label}] ${d.title}${d.reasoning ? ' | ' + d.reasoning : ''}`
            : ''
      }
      if (text.trim()) addSystemMessage(text)
    }
  }, [messages, addSystemMessage])

  // ── Connection status ──────────────────────────────────────────────────

  useEffect(() => {
    if (!startupDone.current) return
    const msg = connected
      ? 'WebSocket connected -- receiving live agent feed'
      : 'WebSocket disconnected -- attempting reconnect...'
    addSystemMessage(msg)
  }, [connected]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-scroll (skip if user scrolled up manually) ─────────────────

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const handleScroll = () => {
      const threshold = 80
      userScrolledUp.current = el.scrollHeight - el.scrollTop - el.clientHeight > threshold
    }
    el.addEventListener('scroll', handleScroll)
    return () => el.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (el && !userScrolledUp.current) {
      el.scrollTop = el.scrollHeight
    }
  }, [history])

  // ── Focus input on mount and click ─────────────────────────────────────

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Fetch the full command catalog + the user's tickers once, for autocomplete.
  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const { data } = await api.get('/api/terminal/commands/all')
        const cmds: CommandEntry[] = (data?.commands ?? []).map((c: { command: string; description?: string; canonical?: string }) => ({
          cmd: c.command,
          alias: c.canonical ?? null,
          desc: c.description ?? '',
        }))
        if (alive && cmds.length) setAllCommands(cmds)
      } catch { /* keep static fallback */ }
      try {
        // Holdings symbols (from accounts summary) + watchlist → ticker pool.
        const [summary, watch] = await Promise.all([
          api.get('/api/accounts/summary').catch(() => ({ data: null })),
          api.get('/api/watchlist').catch(() => ({ data: null })),
        ])
        const pool = new Set<string>()
        for (const h of summary.data?.top_holdings ?? []) if (h?.ticker) pool.add(String(h.ticker).toUpperCase())
        for (const w of watch.data?.items ?? watch.data ?? []) {
          const sym = w?.symbol ?? w?.ticker
          if (sym) pool.add(String(sym).toUpperCase())
        }
        if (alive && pool.size) setTickerPool(Array.from(pool).sort())
      } catch { /* tickers are optional */ }
    })()
    return () => { alive = false }
  }, [])

  const focusInput = useCallback(() => {
    inputRef.current?.focus()
  }, [])

  // ── Autocomplete suggestions ──────────────────────────────────────────
  // Two modes:
  //   1. command  — "/cha" → fuzzy-match command names across the full catalog
  //   2. argument — "/quote AAP" → complete the ticker from holdings/watchlist
  const suggestions = useMemo<CommandEntry[]>(() => {
    if (!input.startsWith('/')) return []

    const spaceIdx = input.indexOf(' ')

    // ── Argument completion: a ticker after a ticker-arg command ──────────
    if (spaceIdx > 0) {
      const cmdWord = input.slice(0, spaceIdx).toLowerCase()
      const rest = input.slice(spaceIdx + 1)
      // Only complete the FIRST argument, and only if there's no further space.
      if (rest.includes(' ') || !TICKER_ARG_COMMANDS.has(cmdWord) || !tickerPool.length) return []
      const q = rest.toUpperCase()
      return tickerPool
        .map(t => ({ t, s: fuzzyScore(q, t) }))
        .filter(x => x.s >= 0)
        .sort((a, b) => b.s - a.s)
        .slice(0, 8)
        .map(x => ({ cmd: `${cmdWord} ${x.t}`, alias: null, desc: 'your holding / watchlist' }))
    }

    // ── Command-name completion (fuzzy across full catalog) ───────────────
    const query = input
    return allCommands
      .map(c => {
        const aliasHit = c.alias
          ? c.alias.split(' ').reduce((best, a) => {
              const al = a.startsWith('/') ? a : '/' + a
              return Math.max(best, fuzzyScore(query, al))
            }, -1)
          : -1
        return { c, score: Math.max(fuzzyScore(query, c.cmd), aliasHit) }
      })
      .filter(x => x.score >= 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8)
      .map(x => x.c)
  }, [input, allCommands, tickerPool])

  // Inline ghost-text: the remaining chars of the top suggestion's command,
  // shown greyed after the cursor and acceptable with Tab / →.
  const ghost = useMemo(() => {
    if (!showSuggestions || !suggestions.length) return ''
    const top = suggestions[selectedSuggestion]?.cmd ?? ''
    return top.toLowerCase().startsWith(input.toLowerCase()) ? top.slice(input.length) : ''
  }, [showSuggestions, suggestions, selectedSuggestion, input])

  useEffect(() => {
    setShowSuggestions(suggestions.length > 0 && input.length > 0)
    setSelectedSuggestion(0)
  }, [suggestions, input])

  // ── Handle key events ──────────────────────────────────────────────────

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Ctrl+L: clear
      if (e.ctrlKey && e.key === 'l') {
        e.preventDefault()
        clear()
        return
      }

      // Ctrl+R: open reverse history search
      if (e.ctrlKey && (e.key === 'r' || e.key === 'R') && !e.shiftKey) {
        e.preventDefault()
        setReverseOpen(true)
        return
      }

      // Enter: execute or select suggestion (but not if one is already running)
      if (e.key === 'Enter') {
        e.preventDefault()
        if (executing) return
        if (input.trim()) {
          const cmd = input.trim()
          executeCommand(cmd)
          addSearch(cmd)
          setInput('')
          setHistoryIndex(-1)
          setShowSuggestions(false)
        }
        return
      }

      // Tab: accept the highlighted suggestion. Command-name completions get a
      // trailing space (ready for an arg); a ticker-arg completion is terminal.
      if (e.key === 'Tab') {
        e.preventDefault()
        if (suggestions.length > 0) {
          const selected = suggestions[selectedSuggestion]
          const isArg = selected.cmd.includes(' ')
          setInput(isArg ? selected.cmd : selected.cmd + ' ')
          setShowSuggestions(false)
        }
        return
      }

      // → at end of line accepts the inline ghost completion (shell-style).
      if (e.key === 'ArrowRight' && ghost) {
        const el = e.currentTarget
        if (el.selectionStart === input.length && el.selectionEnd === input.length) {
          e.preventDefault()
          const top = suggestions[selectedSuggestion]?.cmd ?? ''
          setInput(top.includes(' ') ? top : top + ' ')
          setShowSuggestions(false)
          return
        }
      }

      // Up/Down in suggestions
      if (showSuggestions && suggestions.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          setSelectedSuggestion(prev => Math.min(prev + 1, suggestions.length - 1))
          return
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault()
          setSelectedSuggestion(prev => Math.max(prev - 1, 0))
          return
        }
      }

      // Up/Down for command history (when no suggestions shown)
      if (e.key === 'ArrowUp' && !showSuggestions) {
        e.preventDefault()
        if (commandHistory.length === 0) return
        const newIndex =
          historyIndex === -1
            ? commandHistory.length - 1
            : Math.max(0, historyIndex - 1)
        setHistoryIndex(newIndex)
        setInput(commandHistory[newIndex])
        return
      }
      if (e.key === 'ArrowDown' && !showSuggestions) {
        e.preventDefault()
        if (historyIndex === -1) return
        const newIndex = historyIndex + 1
        if (newIndex >= commandHistory.length) {
          setHistoryIndex(-1)
          setInput('')
        } else {
          setHistoryIndex(newIndex)
          setInput(commandHistory[newIndex])
        }
        return
      }

      // Escape: close suggestions
      if (e.key === 'Escape') {
        setShowSuggestions(false)
        return
      }
    },
    [input, ghost, showSuggestions, suggestions, selectedSuggestion, commandHistory, historyIndex, executeCommand, clear, addSearch],
  )

  // ── Line styling ──────────────────────────────────────────────────────

  const lineClass = (line: TerminalLine) => {
    switch (line.type) {
      case 'command':
        return 'text-crt-bright'
      case 'error':
        return 'text-red-400'
      case 'system':
        return 'text-crt-dim'
      case 'quote': {
        // Check for color hint in data
        const color = (line.data as Record<string, unknown>)?.data
          ? ((line.data as { data?: Record<string, unknown> })?.data as Record<string, unknown>)?.color
          : undefined
        if (color === 'red') return 'text-red-400'
        if (color === 'green') return 'text-crt-green'
        return 'text-crt-amber'
      }
      case 'chart':
        return 'text-crt-cyan'
      case 'dashboard':
        return 'text-crt-green'
      case 'table':
        return 'text-crt-green'
      case 'text':
      default:
        return 'text-crt-green'
    }
  }

  return (
    <div className={clsx('crt-screen animate-fade-in', themeClass(theme))} onClick={focusInput}>
      {/* Scanline overlay */}
      <div className="crt-scanlines" aria-hidden />
      <div className="crt-glow" aria-hidden />

      {/* Command palette (Ctrl+K / Cmd+K) */}
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onSelect={handlePaletteSelect}
      />

      {/* Live ticker strip — click a symbol to /set it as context */}
      <TickerStrip onSymbolClick={sym => {
        executeCommand(`/set ${sym}`)
        addSearch(`/set ${sym}`)
      }} />

      {/* F1-F12 user-mapped shortcuts (floating legend + rebind UI) */}
      <FKeyShortcuts onExecute={cmd => {
        executeCommand(cmd)
        addSearch(cmd)
      }} />

      {/* Multi-pane split view — Ctrl+Shift+M */}
      <MultiPaneView open={multiPaneOpen} onClose={() => setMultiPaneOpen(false)} />

      {/* Shortcut cheat-sheet — `?` key */}
      <ShortcutOverlay open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />

      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-crt-green/30">
        <div className="flex items-center gap-3">
          <span className="text-crt-green font-mono text-sm font-bold">AGENTVAULT TERMINAL</span>
          <span
            className={`inline-block w-2 h-2 rounded-full ${
              connected ? 'bg-crt-green crt-text-glow' : 'bg-red-500'
            }`}
          />
          <span className="text-crt-dim font-mono text-xs">
            {connected ? 'CONNECTED' : 'DISCONNECTED'}
          </span>
          <span className="text-crt-dim/60 font-mono text-xs">|</span>
          <NavIndicator
            onClick={() => {
              executeCommand('/portfolio')
              addSearch('/portfolio')
            }}
          />
          <ConcentrationChip
            onClick={() => {
              executeCommand('/concentration')
              addSearch('/concentration')
            }}
          />
        </div>
        <div className="flex items-center gap-4">
          {executing && (
            <span className="text-crt-amber font-mono text-xs animate-pulse">EXECUTING...</span>
          )}
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="text-crt-dim hover:text-crt-green font-mono text-xs transition-colors flex items-center gap-1"
            title="Fuzzy command palette (Ctrl+K)"
          >
            <kbd className="text-[10px] px-1 py-0.5 border border-crt-dim/40 rounded">Ctrl</kbd>
            <kbd className="text-[10px] px-1 py-0.5 border border-crt-dim/40 rounded">K</kbd>
          </button>
          <button
            type="button"
            onClick={() => setMultiPaneOpen(true)}
            className="text-crt-dim hover:text-crt-green font-mono text-xs transition-colors flex items-center gap-1"
            title="Multi-pane split view (Ctrl+Shift+M)"
          >
            <kbd className="text-[10px] px-1 py-0.5 border border-crt-dim/40 rounded">⊞</kbd>
            <span>2×2</span>
          </button>
          <button
            type="button"
            onClick={() => setShortcutsOpen(true)}
            className="text-crt-dim hover:text-crt-green font-mono text-xs transition-colors flex items-center gap-1"
            title="Keyboard shortcuts (?)"
          >
            <kbd className="text-[10px] px-1 py-0.5 border border-crt-dim/40 rounded">?</kbd>
          </button>
          <SnapshotButton history={history} />
          <ThemePicker theme={theme} onChange={setTheme} />
          <span className="text-crt-dim font-mono text-xs">{history.length} lines</span>
        </div>
      </div>

      {/* Terminal output */}
      <div
        ref={scrollRef}
        className="crt-output flex-1 overflow-y-auto px-4 py-3 font-mono text-sm leading-relaxed crt-scrollbar"
        role="log"
        aria-live="polite"
        aria-label="Terminal output"
      >
        {history.map(line => {
          const pinnable = line.type !== 'command' && line.type !== 'system'
          const pinned = pinnable && isPinned(line)
          return (
            <div key={line.id} className="group relative py-px hover:bg-crt-green/5">
              {line.type === 'command' ? (
                <div className="flex gap-2">
                  <span className="text-crt-dim shrink-0">[{line.timestamp}]</span>
                  <span className="text-crt-bright shrink-0">{'>'}</span>
                  <span className="text-crt-bright">{line.content}</span>
                </div>
              ) : line.type === 'system' ? (
                <div className="flex gap-2">
                  <span className="text-crt-dim shrink-0">[{line.timestamp}]</span>
                  <span className="text-crt-dim shrink-0">SYS</span>
                  <span className="text-crt-dim">|</span>
                  <RichLine text={line.content} className="text-crt-dim whitespace-pre" />
                </div>
              ) : (
                <div className={`whitespace-pre ${lineClass(line)}`}>
                  <RichLine text={line.content} />
                </div>
              )}
              {pinnable && (
                <button
                  type="button"
                  aria-label={pinned ? 'Unpin line' : 'Pin line'}
                  title={pinned ? 'Unpin (remove from /pinned)' : 'Pin (adds to /pinned)'}
                  onClick={e => {
                    e.stopPropagation()
                    if (pinned) unpinLine(line)
                    else pinLine(line)
                    setPinTick(t => t + 1)
                  }}
                  className={clsx(
                    'absolute right-1 top-0 font-mono text-xs px-1 transition-opacity',
                    pinned
                      ? 'opacity-100 text-crt-amber'
                      : 'opacity-0 group-hover:opacity-60 text-crt-dim hover:text-crt-amber',
                  )}
                >
                  {pinned ? '★' : '☆'}
                </button>
              )}
            </div>
          )
        })}
        {/* Unused tick to ensure pin state re-renders */}
        <span className="hidden" aria-hidden>{pinTick}</span>

        {/* Blinking cursor when idle */}
        {!executing && history.length > 0 && (
          <div className="h-1" />
        )}
      </div>

      {/* Autocomplete suggestions */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="px-4 pb-1 border-t border-crt-green/10">
          <div className="font-mono text-xs py-1 flex flex-wrap gap-x-4 gap-y-0.5">
            {suggestions.map((s, i) => (
              <button
                key={s.cmd}
                type="button"
                onClick={() => {
                  setInput(s.cmd + ' ')
                  setShowSuggestions(false)
                  inputRef.current?.focus()
                }}
                className={`px-1 py-0.5 rounded transition-colors ${
                  i === selectedSuggestion
                    ? 'text-crt-bright bg-crt-green/20'
                    : 'text-crt-dim hover:text-crt-green'
                }`}
              >
                {s.cmd}
                {s.alias && <span className="text-crt-dim ml-1">({s.alias})</span>}
                <span className="text-crt-dim ml-2">{s.desc}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Ctrl+R reverse history search (inline above input) */}
      <ReverseSearch
        open={reverseOpen}
        history={commandHistory}
        onAccept={cmd => {
          setInput(cmd)
          setHistoryIndex(-1)
          setShowSuggestions(false)
          setTimeout(() => inputRef.current?.focus(), 0)
        }}
        onClose={() => {
          setReverseOpen(false)
          setTimeout(() => inputRef.current?.focus(), 0)
        }}
      />

      {/* Command input */}
      <div className="flex items-center gap-2 px-4 py-2 border-t border-crt-green/30 bg-black/30">
        <span className="text-crt-green font-mono text-sm font-bold shrink-0">
          {executing ? '...' : '>'}
        </span>
        <div className="relative flex-1">
          {/* Inline ghost completion — mirrors typed text (transparent) then
              shows the remaining chars dimmed. Pointer-events off so it never
              intercepts clicks/selection. */}
          {ghost && (
            <div
              className="pointer-events-none absolute inset-0 font-mono text-sm whitespace-pre overflow-hidden"
              aria-hidden
            >
              <span className="invisible">{input}</span>
              <span className="text-crt-dim/60">{ghost}</span>
            </div>
          )}
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => {
              setInput(e.target.value)
              setHistoryIndex(-1)
            }}
            onKeyDown={handleKeyDown}
            placeholder={executing ? 'executing... (you can still type)' : '/command or type /help'}
            spellCheck={false}
            autoComplete="off"
            className={clsx(
              'relative w-full bg-transparent font-mono text-sm text-crt-green placeholder-crt-dim/50',
              'border-none outline-none caret-crt-green',
              executing && 'opacity-60',
            )}
            aria-label="Terminal command input"
          />
        </div>
        <span className="crt-cursor" aria-hidden />
      </div>
    </div>
  )
}

// ── Helpers ────────────────────────────────────────────────────────────────

function now() {
  return new Date().toLocaleTimeString('en-GB', { hour12: false })
}
